import { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";

import PageTemplate from "../components/PageTemplate";
import WideContent from "../components/WideContent";
import PhotoAlbum from "react-photo-album";
import "react-photo-album/columns.css";

import Lightbox from "yet-another-react-lightbox";
import "yet-another-react-lightbox/styles.css";

import {
  Fullscreen,
  Slideshow,
  Download,
} from "yet-another-react-lightbox/plugins";

import PageFade from "../motions/PageFade";

// Full photos
import highlights from "../highlightsPhotos.json";
import details from "../detailsPhotos.json";
import gettingReady from "../gettingReadyPhotos.json";
import firstLook from "../firstLookPhotos.json";
import preCeremony from "../preCeremonyPhotos.json";
import ceremony from "../ceremonyPhotos.json";
import cocktailHour from "../cocktailHourPhotos.json";
import teaCeremony from "../teaCeremonyPhotos.json";
import reception from "../receptionPhotos.json";
import portraits from "../portraitsPhotos.json";

// Thumbnails
import highlightsThumbs from "../highlightsThumbnailsPhotos.json";
import detailsThumbs from "../detailsThumbnailsPhotos.json";
import gettingReadyThumbs from "../gettingReadyThumbnailsPhotos.json";
import firstLookThumbs from "../firstLookThumbnailsPhotos.json";
import preCeremonyThumbs from "../preCeremonyThumbnailsPhotos.json";
import ceremonyThumbs from "../ceremonyThumbnailsPhotos.json";
import cocktailHourThumbs from "../cocktailHourThumbnailsPhotos.json";
import teaCeremonyThumbs from "../teaCeremonyThumbnailsPhotos.json";
import receptionThumbs from "../receptionThumbnailsPhotos.json";
import portraitsThumbs from "../portraitsThumbnailsPhotos.json";

const galleryMap = {
  "Wedding Highlights": {
    slug: "wedding-highlights",
    thumbnails: highlightsThumbs,
    full: highlights,
  },
  Details: {
    slug: "details",
    thumbnails: detailsThumbs,
    full: details,
  },
  "Getting Ready": {
    slug: "getting-ready",
    thumbnails: gettingReadyThumbs,
    full: gettingReady,
  },
  "First Look": {
    slug: "first-look",
    thumbnails: firstLookThumbs,
    full: firstLook,
  },
  "Pre-Ceremony": {
    slug: "pre-ceremony",
    thumbnails: preCeremonyThumbs,
    full: preCeremony,
  },
  Ceremony: {
    slug: "ceremony",
    thumbnails: ceremonyThumbs,
    full: ceremony,
  },
  "Cocktail Hour": {
    slug: "cocktail-hour",
    thumbnails: cocktailHourThumbs,
    full: cocktailHour,
  },
  "Tea Ceremony": {
    slug: "tea-ceremony",
    thumbnails: teaCeremonyThumbs,
    full: teaCeremony,
  },
  Reception: {
    slug: "reception",
    thumbnails: receptionThumbs,
    full: reception,
  },

  // NEW: Portrait Lounge
  "Portrait Lounge": {
    slug: "portrait-lounge",
    thumbnails: portraitsThumbs,
    full: portraits,
  },

  // Wedding Film
  "Wedding Film": {
    slug: "wedding-film",
    isVideo: true,
  },
};

const categories = Object.keys(galleryMap);

function PhotoGallery() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [index, setIndex] = useState(-1);

  const tabParam = searchParams.get("tab");

  const activeTab =
    categories.find((cat) => galleryMap[cat].slug === tabParam) ||
    "Wedding Highlights";

  const activeConfig = galleryMap[activeTab];
  const isVideoTab = activeConfig?.isVideo;

  const thumbnails = activeConfig?.thumbnails || [];
  const full = activeConfig?.full || [];

  const getNextTab = (currentTab) => {
    const currentIndex = categories.indexOf(currentTab);
    const nextIndex = (currentIndex + 1) % categories.length;
    return categories[nextIndex];
  };

  const changeTab = (tabName) => {
    setSearchParams({
      tab: galleryMap[tabName].slug,
    });

    setIndex(-1);
  };

  const handleShowMore = () => {
    const nextTab = getNextTab(activeTab);

    setSearchParams({
      tab: galleryMap[nextTab].slug,
    });

    setIndex(-1);

    setTimeout(() => {
      window.scrollTo({
        top: 0,
        behavior: "smooth",
      });
    }, 50);
  };

  return (
    <PageFade>
      <PageTemplate title="Wedding Gallery" textColor="text-gold">
        <WideContent>
          {/* Intro */}
          <div className="space-y-6">
            <div className="max-w-4xl mx-auto text-center space-y-4 px-4">
              <p>
                To our family and friends, thank you for celebrating with us!
                Whether you traveled from near or far, your presence filled our
                day with laughter and love.
              </p>

              <p className="text-sm">
                Click through the tabs below to explore each moment of our
                wedding day.
              </p>
            </div>

            {/* Tabs */}
            <div className="flex flex-wrap justify-center gap-2 border-b border-gold/30 pb-3 mx-auto">
              {categories.map((cat) => (
                <button
                  key={cat}
                  onClick={() => changeTab(cat)}
                  className={`px-3 py-1 text-sm rounded-md transition ${activeTab === cat
                    ? "bg-gold text-white"
                    : "text-gold hover:bg-gold/10"
                    }`}
                >
                  {cat}
                </button>
              ))}
            </div>
          </div>

          {/* Gallery / Video */}
          <div className="container p-8 lg:p-10 xl:p-16 text-center mx-auto">
            {isVideoTab ? (
              <div className="w-full flex flex-col items-center space-y-10">
                <div className="max-w-2xl text-center space-y-2">
                  <p className="font-medium">
                    Relive our wedding day through our films.
                  </p>
                  <p className="text-sm">
                    Enjoy our trailer and full wedding film below.
                  </p>
                </div>

                {/* Wedding Trailer */}
                <div className="w-full max-w-5xl space-y-3">
                  <h3 id="wedding-tailer" className="font-medium text-gold">Wedding Trailer</h3>

                  <div className="aspect-video">
                    <iframe
                      src="https://player.vimeo.com/video/1192989669?h=0be32e12ef"
                      title="Wedding Film Trailer"
                      className="w-full h-full rounded-md"
                      frameBorder="0"
                      allow="autoplay; fullscreen; picture-in-picture; clipboard-write; encrypted-media; web-share"
                      allowFullScreen
                    />
                  </div>
                </div>

                {/* Full Wedding Film */}
                <div className="w-full max-w-5xl space-y-3">
                  <h3 id="full-wedding-film" className="font-medium text-gold">Full Wedding Film</h3>

                  <div className="aspect-video">
                    <iframe
                      src="https://player.vimeo.com/video/1193464566?h=aa5aac6403"
                      title="Full Wedding Film"
                      className="w-full h-full rounded-md"
                      frameBorder="0"
                      allow="autoplay; fullscreen; picture-in-picture; clipboard-write; encrypted-media; web-share"
                      allowFullScreen
                    />
                  </div>
                </div>
              </div>
            ) : (
              <>
                <PhotoAlbum
                  photos={thumbnails}
                  spacing={5}
                  layout="rows"
                  {...(activeTab !== "Portrait Lounge" && {
                    targetRowHeight: 450,
                  })} onClick={({ index }) => setIndex(index)}
                />

                <Lightbox
                  slides={full}
                  open={index >= 0}
                  index={index}
                  close={() => setIndex(-1)}
                  plugins={[Fullscreen, Slideshow, Download]}
                  styles={{
                    container: {
                      backgroundColor: "rgba(0, 0, 0, 0.9)",
                    },
                  }}
                />

                <div className="flex justify-center mt-8">
                  <button
                    onClick={handleShowMore}
                    className="px-6 py-2 bg-gold text-white rounded-md hover:bg-gold/90 transition"
                  >
                    View more →
                  </button>
                </div>
              </>
            )}
          </div>
        </WideContent>
      </PageTemplate>
    </PageFade>
  );
}

export default PhotoGallery;